import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { auth as authApi } from "../services/auth";
import { setAccessToken } from "../services/api";
import "../components/AuthForm.css";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setVerifying(false); return; }
      const res = await authApi.verifyResetToken(token);
      if (cancelled) return;
      setTokenValid(res.ok);
      if (res.ok && res.data.email) setEmail(res.data.email);
      setVerifying(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setSubmitting(true);
    const res = await authApi.resetPassword(token, password);
    setSubmitting(false);
    if (res.ok && res.data.token) {
      // Server also issued a fresh refresh cookie (Set-Cookie) — browser
      // stores it automatically. Just need to seed the in-memory access token.
      setAccessToken(res.data.token);
      navigate("/dashboard");
    } else if (res.ok) {
      setError("Reset failed");
    } else {
      setError(res.error.message);
    }
  }

  if (verifying) {
    return (
      <section className="auth-shell container">
        <div className="auth-card card">
          <p style={{ color: "var(--text-secondary)" }}>Verifying reset link…</p>
        </div>
      </section>
    );
  }

  if (!tokenValid) {
    return (
      <section className="auth-shell container">
        <div className="auth-card card">
          <h1>Link expired</h1>
          <p className="auth-card__sub">
            This reset link is invalid or has expired. Request a fresh one — they're valid for 10 minutes.
          </p>
          <Link to="/forgot-password" className="btn btn--primary">Request new link</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-shell container">
      <div className="auth-card card">
        <h1>Set a new password</h1>
        <p className="auth-card__sub">{email ? `for ${email}` : "Choose a strong password."}</p>
        <form className="form" onSubmit={onSubmit}>
          {error && <div className="form__alert" role="alert">{error}</div>}
          <div className="field">
            <label htmlFor="password">New password</label>
            <input id="password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input id="confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </section>
  );
}
